import React, { useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, Eye, Star, X, Clock, Upload, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Table from '../components/Table';
import Modal from '../components/Modal';
import Form, { FormField, useForm } from '../components/Form';
import FilterBar from '../components/FilterBar';
import { StatusToggleCell, VerifiedToggleCell } from '../components/StatusBadge';
import { useFetch, useFilters } from '../hooks';
import { doctorService, specializationService, qualificationService, timeSlotService } from '../services/api';
import { formatCurrency } from '../utils/helpers';
import { getCurrencyConfig } from '../utils/currencyConfig';

/* ── Static fallbacks (used only if Master Management has no active data) ───── */
const FALLBACK_SPECIALTIES = [
  'Cardiologist', 'Neurologist', 'Pediatrician', 'Orthopedic',
  'Dermatologist', 'General Physician', 'Psychiatrist', 'Oncologist',
];

const FILTER_INITIAL = { search: '', specialty: '', status: '', verified: '' };

const FILTER_FIELDS = [
  {
    key: 'specialty', label: 'Specialization', type: 'select',
    options: FALLBACK_SPECIALTIES.map(s => ({ value: s, label: s })),
  },
  {
    key: 'status', label: 'Status', type: 'select',
    options: [
      { value: 'active',   label: 'Active'   },
      { value: 'inactive', label: 'Inactive' },
      { value: 'pending',  label: 'Pending'  },
    ],
  },
  {
    key: 'verified', label: 'Verification', type: 'select',
    options: [{ value: 'true', label: 'Verified' }, { value: 'false', label: 'Unverified' }],
  },
];

/* ── Filter function ────────────────────────────────────────────────────────── */
const doctorFilterFn = (doc, f) => {
  if (f.search) {
    const q = f.search.toLowerCase();
    const haystack = [doc.name, doc.email, doc.mobile, doc.specialization].join(' ').toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  if (f.specialty && doc.specialization !== f.specialty) return false;
  if (f.status && doc.status !== f.status) return false;
  if (f.verified !== '') {
    const want = f.verified === 'true';
    if (!!doc.is_verified !== want) return false;
  }
  return true;
};

/* ── MultiSelect ────────────────────────────────────────────────────────────── */
const MultiSelect = ({ label, options, selected, onChange }) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
    <div className="flex flex-wrap gap-1.5 p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 min-h-[42px]">
      {selected.map(s => (
        <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 bg-medihub-light dark:bg-medihub-dark/40 text-medihub-secondary dark:text-medihub-highlight rounded-md text-xs font-medium">
          {s}
          <button type="button" onClick={() => onChange(selected.filter(x => x !== s))} className="hover:text-red-500 transition-colors">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <select
        className="flex-1 min-w-[140px] text-sm bg-transparent outline-none text-gray-500 dark:text-gray-400"
        value=""
        onChange={e => { if (e.target.value && !selected.includes(e.target.value)) onChange([...selected, e.target.value]); }}
      >
        <option value="">+ Add specialty</option>
        {options.filter(o => !selected.includes(o)).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  </div>
);

/* ── SlotSelector — driven by active slots from Master Management ───────────── */
const SlotSelector = ({ slots, selected, onChange }) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
      Available Time Slots
      {slots.length === 0 && (
        <span className="text-xs text-orange-500 font-normal ml-2">
          (No active slots — add from Master Management)
        </span>
      )}
    </label>
    {slots.length === 0 ? (
      <p className="text-xs text-gray-400 italic px-1">No active time slots available.</p>
    ) : (
      <div className="flex flex-wrap gap-2">
        {slots.map(slot => (
          <button
            key={slot.label}
            type="button"
            onClick={() => onChange(
              selected.includes(slot.label)
                ? selected.filter(s => s !== slot.label)
                : [...selected, slot.label]
            )}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors border ${
              selected.includes(slot.label)
                ? 'bg-medihub-primary text-white border-medihub-primary'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-medihub-highlight'
            }`}
          >
            {slot.label}
          </button>
        ))}
      </div>
    )}
  </div>
);

/* ── Availability Modal ────────────────────────────────────────────────── */
const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

const AvailabilityModal = ({ doctor, onClose }) => {
  const [availability, setAvailability] = useState(
    DAYS.map(day => ({ day, is_available: 0, slots: [] }))
  );
  const [saving, setSaving] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(true);

  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await doctorService.getAvailability(doctor.id);
        if (res?.length) {
          setAvailability(DAYS.map(day => {
            const found = res.find(d => d.day === day);
            return found
              ? { day, is_available: found.is_available, slots: found.slots || [] }
              : { day, is_available: 0, slots: [] };
          }));
        }
      } catch { /* keep default empty state */ }
      finally { setLoadingSlots(false); }
    };
    load();
  }, [doctor.id]);

  const toggleDay = (day) => {
    setAvailability(prev => prev.map(d =>
      d.day === day ? { ...d, is_available: d.is_available ? 0 : 1 } : d
    ));
  };

  const addSlot = (day) => {
    setAvailability(prev => prev.map(d =>
      d.day === day ? { ...d, slots: [...d.slots, { start_time: '09:00', end_time: '10:00' }] } : d
    ));
  };

  const removeSlot = (day, idx) => {
    setAvailability(prev => prev.map(d =>
      d.day === day ? { ...d, slots: d.slots.filter((_, i) => i !== idx) } : d
    ));
  };

  const updateSlot = (day, idx, field, value) => {
    setAvailability(prev => prev.map(d =>
      d.day === day
        ? { ...d, slots: d.slots.map((s, i) => i === idx ? { ...s, [field]: value } : s) }
        : d
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await doctorService.saveAvailability(doctor.id, availability);
      toast.success(res?.message || 'Availability saved!');
      onClose();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Availability — ${doctor.name}`} size="lg">
      {loadingSlots ? (
        <div className="flex items-center justify-center py-12 text-gray-400 text-sm">Loading availability…</div>
      ) : (
        <>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        {availability.map(({ day, is_available, slots }) => (
          <div key={day} className={`border rounded-xl p-4 transition-colors ${
            is_available ? 'border-medihub-primary dark:border-medihub-accent bg-medihub-light/30 dark:bg-medihub-dark/10' : 'border-gray-200 dark:border-gray-700'
          }`}>
            {/* Day header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => toggleDay(day)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    is_available ? 'bg-medihub-primary' : 'bg-gray-300 dark:bg-gray-600'
                  }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    is_available ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <span className="text-sm font-semibold text-gray-900 dark:text-white capitalize">{day}</span>
                {!is_available && <span className="text-xs text-gray-400">Unavailable</span>}
              </div>
              {is_available && (
                <button type="button" onClick={() => addSlot(day)}
                  className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg bg-medihub-primary text-white hover:bg-medihub-secondary transition-colors">
                  <Plus className="w-3 h-3" />Add Slot
                </button>
              )}
            </div>

            {/* Slots */}
            {is_available && (
              <div className="space-y-2">
                {slots.length === 0 && (
                  <p className="text-xs text-gray-400 italic">No slots added — click "Add Slot"</p>
                )}
                {slots.map((slot, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input type="time" value={slot.start_time}
                      onChange={e => updateSlot(day, idx, 'start_time', e.target.value)}
                      className="input-field flex-1 text-sm" />
                    <span className="text-xs text-gray-400">to</span>
                    <input type="time" value={slot.end_time}
                      onChange={e => updateSlot(day, idx, 'end_time', e.target.value)}
                      className="input-field flex-1 text-sm" />
                    {/* <button type="button" onClick={() => removeSlot(day, idx)}
                      className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-red-500 transition-colors">
                      <X className="w-4 h-4" />
                    </button> */}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button onClick={handleSave} disabled={saving}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? 'Saving...' : 'Save Availability'}
        </button>
      </div>
        </>
      )}
    </Modal>
  );
};

/* ── DoctorForm — receives active specialties & slots as props ──────────────── */
const DoctorForm = ({ initial, activeSpecialties, activeQualifications, activeSlots, onSubmit, onClose }) => {
  const [showOtherQualification, setShowOtherQualification] = useState(false);
  const [otherQualification, setOtherQualification] = useState('');
  const rules = {
    name:     { required: true },
    email:    { required: true, email: true },
    mobile:   { required: true, mobile: true },
    clinic_phone: { required: true, mobile: true },
    specialization: { required: true },
    // password: initial ? {} : { required: true },
  };
  const { values, errors, handleChange, handleBlur, handleSubmit, setValues } = useForm(
    initial ? { ...initial } : {
      name: 'Dr. ', email: '', mobile: '', password: '',
      specialization: '', qualifications: '', experience_years: '',
      consultation_fee: '', consultancy_type: 'online',
      address: '', commission_percent: '', status: 'active', profile_img: null,
      registration_number: '', medical_council_name: '', clinic_phone: '',
      clinic_name: '', city: '', bio: '',
      registration_certificate_url: null, degree_certificate_url: null, id_proof_url: null,
    },
    rules
  );

  const handleQualificationChange = (e) => {
    const val = e.target.value;
    if (val === 'others') {
      setShowOtherQualification(true);
      setValues(p => ({ ...p, qualifications: '' }));
    } else {
      setShowOtherQualification(false);
      setOtherQualification('');
      handleChange(e);
    }
  };

  const handleOtherQualificationChange = (e) => {
    const val = e.target.value;
    setOtherQualification(val);
    setValues(p => ({ ...p, qualifications: val }));
  };

  return (
    <Form onSubmit={handleSubmit((vals) => onSubmit(vals))}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Full Name" error={errors.name} required>
          <input
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            className="input-field"
            placeholder="Dr. John Doe"
          />
        </FormField>
        <FormField label="Email" error={errors.email} required>
          <input
            name="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className="input-field"
            placeholder="doctor@medihub.com"
          />
        </FormField>
        <FormField label="Mobile" error={errors.mobile} required>
          <input
            name="mobile"
            value={values.mobile}
            onChange={handleChange}
            onBlur={handleBlur}
            className="input-field"
            placeholder="9876543210"
          />
        </FormField>
        {/* {!initial && (
          <FormField label="Password" error={errors.password} required>
            <input name="password" type="password" value={values.password} onChange={handleChange} onBlur={handleBlur} className="input-field" placeholder="••••••••" />
          </FormField>
        )} */}
        <FormField
          label="Specialization"
          error={errors.specialization}
          required
        >
          <select
            name="specialization"
            value={values.specialization}
            onChange={handleChange}
            onBlur={handleBlur}
            className="input-field"
          >
            <option value="">Select specialization</option>
            {activeSpecialties.map((s) => (
              <option
                key={typeof s === "string" ? s : s.id}
                value={typeof s === "string" ? s : s.name}
              >
                {typeof s === "string" ? s : s.name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Qualifications">
          <select
            name="qualifications"
            value={showOtherQualification ? "others" : values.qualifications}
            onChange={handleQualificationChange}
            className="input-field"
          >
            <option value="">Select qualification</option>
            {activeQualifications.map((q) => (
              <option key={q.id} value={q.name}>
                {q.name}
              </option>
            ))}
            <option value="others">Others</option>
          </select>
        </FormField>
        {showOtherQualification && (
          <FormField label="Enter Qualification">
            <input
              value={otherQualification}
              onChange={handleOtherQualificationChange}
              className="input-field"
              placeholder="Enter qualification"
            />
          </FormField>
        )}
        <FormField label="Experience (years)">
          <input
            name="experience_years"
            type="number"
            value={values.experience_years}
            onChange={handleChange}
            className="input-field"
            placeholder="10"
          />
        </FormField>
        <FormField label={`Consultation Fee (${getCurrencyConfig().symbol})`}>
          <input
            name="consultation_fee"
            type="number"
            value={values.consultation_fee}
            onChange={handleChange}
            className="input-field"
            placeholder="500"
          />
        </FormField>
        <FormField label="Consultancy Type">
          <select
            name="consultancy_type"
            value={values.consultancy_type}
            onChange={handleChange}
            className="input-field"
          >
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="both">Both</option>
          </select>
        </FormField>
        <FormField label="Commission (%)">
          <input
            name="commission_percent"
            type="number"
            min="0"
            max="100"
            value={values.commission_percent}
            onChange={handleChange}
            className="input-field"
            placeholder="10"
          />
        </FormField>
        {values.consultation_fee && values.commission_percent && (
          <div className="flex items-center justify-between px-3 py-2 bg-medihub-light dark:bg-medihub-dark/20 rounded-lg">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Commission Amount
            </span>
            <span className="text-sm font-bold text-medihub-primary dark:text-medihub-highlight">
              {getCurrencyConfig().symbol}
              {(
                (parseFloat(values.consultation_fee) *
                  parseFloat(values.commission_percent)) /
                100
              ).toFixed(2)}
            </span>
          </div>
        )}
        <FormField label="Status">
          <select
            name="status"
            value={values.status}
            onChange={handleChange}
            className="input-field"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </FormField>
        <FormField label="Address">
          <input
            name="address"
            value={values.address}
            onChange={handleChange}
            className="input-field"
            placeholder="123, MG Road, Mumbai"
          />
        </FormField>
        <FormField label="City / Area">
          <input
            name="city"
            value={values.city}
            onChange={handleChange}
            className="input-field"
            placeholder="Mumbai"
          />
        </FormField>
      </div>

      {/* Medical Details */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-2">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Medical Registration
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Registration Number">
            <input
              name="registration_number"
              value={values.registration_number}
              onChange={handleChange}
              className="input-field"
              placeholder="MCI-12345"
            />
          </FormField>
          <FormField label="Medical Council Name">
            <input
              name="medical_council_name"
              value={values.medical_council_name}
              onChange={handleChange}
              className="input-field"
              placeholder="Medical Council of India"
            />
          </FormField>
        </div>
      </div>

      {/* Clinic Details */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-2">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Clinic / Hospital Details
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Clinic / Hospital Name">
            <input
              name="clinic_name"
              value={values.clinic_name}
              onChange={handleChange}
              className="input-field"
              placeholder="Apollo Hospital"
            />
          </FormField>
          <FormField label="Clinic Phone">
            <input
              name="clinic_phone"
              value={values.clinic_phone}
              onChange={handleChange}
              className="input-field"
              placeholder="022-12345678"
            />
          </FormField>
        </div>
      </div>

      {/* Bio */}
      <FormField label="Bio / Description">
        <textarea
          name="bio"
          value={values.bio}
          onChange={handleChange}
          className="input-field resize-none"
          rows={3}
          placeholder="Brief description about the doctor..."
        />
      </FormField>

      {/* Documents */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-2">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Documents
        </p>
        <div className="grid grid-cols-1 gap-3">
          {[
            {
              label: "Medical Registration Certificate",
              key: "registration_certificate_url",
            },
            { label: "Degree Certificate", key: "degree_certificate_url" },
            { label: "ID Proof (Aadhar / PAN)", key: "id_proof_url" },
          ].map(({ label, key }) => (
            <FormField key={key} label={label}>
              {values[key] ? (
                <div className="flex items-center gap-2 input-field py-2">
                  <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                    {values[key].name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setValues((p) => ({ ...p, [key]: null }))}
                    className="shrink-0 text-red-500 hover:text-red-700 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer input-field py-2 text-sm text-gray-500 dark:text-gray-400 hover:border-medihub-primary transition-colors">
                  <Upload className="w-4 h-4 shrink-0 text-gray-400" />
                  <span>Upload {label}…</span>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={(e) =>
                      setValues((p) => ({
                        ...p,
                        [key]: e.target.files[0] || null,
                      }))
                    }
                  />
                </label>
              )}
            </FormField>
          ))}
        </div>
      </div>

      <FormField label="Profile Image">
        {values.profile_img ? (
          <div className="flex items-center gap-2 input-field py-2">
            <img
              src={
                values.profile_img instanceof File
                  ? URL.createObjectURL(values.profile_img) // ✅ Nai file
                  : values.profile_img // ✅ Server URL string
              }
              alt=""
              className="w-8 h-8 rounded-lg object-cover shrink-0"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
              {values.profile_img.name}
            </span>
            <button
              type="button"
              onClick={() => setValues((p) => ({ ...p, profile_img: null }))}
              className="shrink-0 text-red-500 hover:text-red-700 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <label className="flex items-center gap-2 cursor-pointer input-field py-2 text-sm text-gray-500 dark:text-gray-400 hover:border-medihub-primary transition-colors">
            <Upload className="w-4 h-4 shrink-0 text-gray-400" />
            <span>Upload Profile Image…</span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) =>
                setValues((p) => ({
                  ...p,
                  profile_img: e.target.files[0] || null,
                }))
              }
            />
          </label>
        )}
      </FormField>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary">
          {initial ? "Update Doctor" : "Add Doctor"}
        </button>
      </div>
    </Form>
  );
};

/* ── Page ───────────────────────────────────────────────────────────────────── */
const Doctors = () => {
  const navigate = useNavigate();
  const { data: doctors, loading, refetch } = useFetch(() => doctorService.getAll());

  // Fetch ONLY active specializations and time slots from Master Management
  const { data: activeSpecData } = useFetch(() => specializationService.getActive());
  const { data: activeQualData } = useFetch(() => qualificationService.getActive());
  const { data: activeSlotData } = useFetch(() => timeSlotService.getActive());

  const activeSpecialties = activeSpecData?.map(s => s.name) || FALLBACK_SPECIALTIES;
  const activeQualifications = activeQualData || [];
  const activeSlots       = activeSlotData || [];

  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [availabilityTarget, setAvailabilityTarget] = useState(null);

  const filterFn = useCallback(doctorFilterFn, []);
  const { filters, onChange: onFilterChange, onReset, filtered, activeCount } = useFilters(FILTER_INITIAL, filterFn, doctors);

  /* ── Handlers ── */
  const handleSave = async (values) => {
    try {
      if (modal === 'edit') {
        const res = await doctorService.update(selected.id, values);
        toast.success(res?.message || 'Doctor updated!');
      } else {
        const res = await doctorService.create(values);
        toast.success(res?.message || 'Doctor added!');
      }
      refetch(); setModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await doctorService.delete(deleteTarget.id);
      toast.success(res?.message || 'Doctor deleted');
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete');
    } finally { setDeleting(false); }
  };

  const handleToggleStatus = async (doc) => {
    const newStatus = doc.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await doctorService.toggleStatus(doc.id, newStatus);
      toast.success(res?.message || `Doctor ${newStatus}d`);
      refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to update'); }
  };

  const handleToggleVerified = async (doc) => {
    const newVerified = doc.is_verified ? 0 : 1;
    try {
      const res = await doctorService.toggleVerified(doc.id, newVerified);
      toast.success(res?.message || `Verification ${newVerified ? 'granted' : 'removed'}`);
      refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to update'); }
  };

  /* ── Columns ── */
  const columns = [
    {
      key: 'name', label: 'Doctor', render: (v, row) => (
        <div className="flex items-center gap-3">
          {row.profile_img
            ? <img src={row.profile_img} alt={v} className="w-9 h-9 rounded-full object-cover shrink-0" />
            : <div className="w-9 h-9 bg-medihub-light dark:bg-medihub-dark/40 rounded-full flex items-center justify-center text-medihub-secondary dark:text-medihub-highlight font-semibold text-sm shrink-0">{v[0]}</div>
          }
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{v}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{row.specialization || '—'}</p>
          </div>
        </div>
      )
    },
    { key: 'qualifications', label: 'Qualifications', render: v => <span className="text-sm">{v || '—'}</span> },
    { key: 'consultation_fee', label: 'Fees', render: v => v ? <span className="font-semibold text-green-600 dark:text-green-400">{formatCurrency(v)}</span> : '—' },
    { key: 'experience_years', label: 'Exp.', render: v => <span className="text-sm">{v} yrs</span> },
    { key: 'rating', label: 'Rating', render: v => v ? <span className="flex items-center gap-1 text-sm"><Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />{v}</span> : '—' },
    {
      key: 'status', label: 'Status', render: (v, row) => (
        <StatusToggleCell active={v === 'active'} onToggle={() => handleToggleStatus(row)} />
      )
    },
    {
      key: 'is_verified', label: 'Verified', render: (v, row) => (
        <VerifiedToggleCell verified={!!v} onToggle={() => handleToggleVerified(row)} />
      )
    },
    {
      key: 'id', label: 'Actions', render: (_, row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => navigate(`/doctors/${row.id}`)} className="p-1.5 hover:bg-medihub-light dark:hover:bg-medihub-dark/20 rounded-lg transition-colors text-medihub-primary" title="View Details">
            <Eye className="w-4 h-4" />
          </button>
          <button onClick={() => setAvailabilityTarget(row)} className="p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors text-blue-500" title="Set Availability">
            <Clock className="w-4 h-4" />
          </button>
          <button onClick={() => { setSelected(row); setModal('edit'); }} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500" title="Edit">
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={() => setDeleteTarget(row)} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500" title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Doctors</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage all registered doctors</p>
        </div>
        <button onClick={() => { setSelected(null); setModal('add'); }} className="btn-primary flex items-center gap-1.5">
          <Plus className="w-4 h-4" />Add Doctor
        </button>
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        onChange={onFilterChange}
        onReset={onReset}
        searchPlaceholder="Search by name, email, phone…"
        fields={FILTER_FIELDS}
        activeCount={activeCount}
      />

      {/* Table */}
      <Table
        title={`Doctors (${filtered.length}${activeCount ? ` of ${doctors?.length || 0}` : ''})`}
        columns={columns}
        data={filtered}
        loading={loading}
        exportFilename="doctors"
      />

      {/* Availability Modal */}
      {availabilityTarget && (
        <AvailabilityModal
          doctor={availabilityTarget}
          onClose={() => setAvailabilityTarget(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Doctor"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to delete <span className="font-semibold text-gray-900 dark:text-white">{deleteTarget?.name}</span>? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setDeleteTarget(null)} className="btn-secondary">Cancel</button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add / Edit Modal */}
      <Modal
        isOpen={modal === 'add' || modal === 'edit'}
        onClose={() => setModal(null)}
        title={modal === 'edit' ? 'Edit Doctor' : 'Add New Doctor'}
        size="lg"
      >
        <DoctorForm
          initial={modal === 'edit' ? selected : null}
          activeSpecialties={activeSpecialties}
          activeQualifications={activeQualifications}
          activeSlots={activeSlots}
          onSubmit={handleSave}
          onClose={() => setModal(null)}
        />
      </Modal>
    </div>
  );
};

export default Doctors;
